import { API_URL } from "../config/api";

export interface ManufacturerInterface {
  id: string;
  name: string;
  description: string;
  website: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  country: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ManufacturersResponse {
  total: number;
  page: number;
  size: number;
  results: ManufacturerInterface[];
}

/**
 * Get all manufacturers
 * @param {string} token - The authorization token
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} size - Number of items per page (default: 100)
 * @returns {Promise<ManufacturersResponse>} List of manufacturers
 */
export const getManufacturers = async (
  token: string,
  page: number = 1,
  size: number = 100
): Promise<ManufacturersResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    const response = await fetch(`${API_URL}/manufacturers?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ManufacturersResponse = await response.json();
    console.log('Manufacturers API response:', data);
    return data;
  } catch (error: Error | any) {
    console.error('Error fetching manufacturers:', error);
    throw new Error(error.message || 'Failed to fetch manufacturers');
  }
};

/**
 * Get manufacturer by ID
 * @param {string} token - The authorization token
 * @param {string} manufacturerId - Manufacturer ID
 * @returns {Promise<ManufacturerInterface>} Manufacturer details
 */
export const getManufacturer = async (
  token: string,
  manufacturerId: string
): Promise<ManufacturerInterface> => {
  try {
    const response = await fetch(`${API_URL}/manufacturers/${manufacturerId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ManufacturerInterface = await response.json();
    return data;
  } catch (error: Error | any) {
    console.error('Error fetching manufacturer:', error);
    throw new Error(error.message || 'Failed to fetch manufacturer');
  }
};

/**
 * Create new manufacturer
 * @param {string} token - The authorization token
 * @param {Omit<ManufacturerInterface, 'id' | 'created_at' | 'updated_at'>} manufacturer - Manufacturer data
 * @returns {Promise<ManufacturerInterface>} Created manufacturer
 */
export const createManufacturer = async (
  token: string,
  manufacturer: Omit<ManufacturerInterface, 'id' | 'created_at' | 'updated_at'>
): Promise<ManufacturerInterface> => {
  try {
    const response = await fetch(`${API_URL}/manufacturers`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(manufacturer)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ManufacturerInterface = await response.json();
    return data;
  } catch (error: Error | any) {
    console.error('Error creating manufacturer:', error);
    throw new Error(error.message || 'Failed to create manufacturer');
  }
};

/**
 * Update manufacturer
 * @param {string} token - The authorization token
 * @param {string} manufacturerId - Manufacturer ID
 * @param {Partial<Omit<ManufacturerInterface, 'id' | 'created_at' | 'updated_at'>>} manufacturer - Updated manufacturer data
 * @returns {Promise<ManufacturerInterface>} Updated manufacturer
 */
export const updateManufacturer = async (
  token: string,
  manufacturerId: string,
  manufacturer: Partial<Omit<ManufacturerInterface, 'id' | 'created_at' | 'updated_at'>>
): Promise<ManufacturerInterface> => {
  try {
    const response = await fetch(`${API_URL}/manufacturers/${manufacturerId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(manufacturer)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ManufacturerInterface = await response.json();
    return data;
  } catch (error: Error | any) {
    console.error('Error updating manufacturer:', error);
    throw new Error(error.message || 'Failed to update manufacturer');
  }
};

/**
 * Delete manufacturer
 * @param {string} token - The authorization token
 * @param {string} manufacturerId - Manufacturer ID
 * @returns {Promise<void>} Success response
 */
export const deleteManufacturer = async (
  token: string,
  manufacturerId: string
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/manufacturers/${manufacturerId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: Error | any) {
    console.error('Error deleting manufacturer:', error);
    throw new Error(error.message || 'Failed to delete manufacturer');
  }
}; 